import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getCurrentWeather } from "../services/api";
import axios from "axios";
import { RootState } from "./store";
import { WeatherData, Location } from "../types";
import {
  selectPrimaryLocation,
  selectPreferredUnits,
} from "./weatherPreferencesSlice";
import api from '../services/api';

// Enhanced types for weather data
export interface WeatherCondition {
  id: number;
  main: string;
  description: string;
  icon: string;
}

export interface Temperature {
  actual: number;
  feelsLike: number;
  min: number;
  max: number;
}

export interface Wind {
  speed: number;
  degree: number;
  gust?: number;
}

export interface Precipitation {
  probability: number;
  intensity?: number;
  type?: "rain" | "snow" | "sleet" | "none";
}

export interface AstronomicalData {
  sunrise: string;
  sunset: string;
  moonPhase: number; // 0-1 where 0 and 1 are new moon, 0.5 is full moon
  moonrise?: string;
  moonset?: string;
  solarNoon?: string;
}

export interface AirQuality {
  index: number; // 1-5 where 1 is good, 5 is hazardous
  components?: {
    co?: number;
    no2?: number;
    o3?: number;
    pm2_5?: number;
    pm10?: number;
  };
}

// Extended WeatherData to include all properties from the imported type
export interface ExtendedWeatherData {
  location: {
    id?: string;
    name: string;
    latitude: number;
    longitude: number;
    country: string;
    timezone: string;
  };
  conditions: WeatherCondition[];
  temperature: Temperature;
  humidity: number;
  pressure: number;
  visibility: number;
  wind: Wind;
  clouds: number; // percentage
  precipitation: Precipitation;
  astronomy: AstronomicalData;
  updatedAt: string;
  forecast?: Array<{
    timestamp: string;
    temperature: number;
    conditions: WeatherCondition[];
    precipitation: Precipitation;
    wind: Wind;
  }>;
  airQuality?: AirQuality;
  alerts?: Array<{
    type: string;
    severity: "minor" | "moderate" | "severe" | "extreme";
    headline: string;
    description: string;
    start: string;
    end: string;
    source: string;
  }>;
  
  // Additional fields from imported WeatherData type
  is_clear?: boolean;
  cloud_percentage?: number;
  condition?: string;
  time?: string;
  viewing_score?: number;
  light_pollution?: number;
  moon_interference?: number;
}

interface WeatherState {
  data: WeatherData | null;
  loading: boolean;
  error: string | null;
  lastUpdated: string;
  cachedLocations: Record<string, {
    data: WeatherData;
    timestamp: string;
  }>;
  autoRefresh: boolean;
  refreshInterval: number;
  locations: Location[];
}

const initialState: WeatherState = {
  data: null,
  loading: false,
  error: null,
  lastUpdated: new Date().toISOString(),
  cachedLocations: {},
  autoRefresh: false,
  refreshInterval: 300000, // 5 minutes
  locations: [],
};

// Fetch weather with location parameter and caching
export const fetchWeather = createAsyncThunk(
  "weather/fetchWeather",
  async (location: Location, { rejectWithValue }) => {
    try {
      const response = await api.get<WeatherData>(`/api/weather/${location.id}`);
      return response.data;
    } catch (error: any) {
      console.error('[WEATHER_SYSTEM] Critical failure in weather data acquisition:', error);
      return rejectWithValue({
        message: error.response?.data?.message || 'Weather system malfunction',
        code: error.response?.status || 500,
        timestamp: new Date().toISOString()
      });
    }
  }
);

// Fetch weather for a specific location and add to cache without setting as current
export const cacheLocationWeather = createAsyncThunk(
  "weather/cacheLocationWeather",
  async (
    params: {
      latitude: number;
      longitude: number;
      locationId: string;
    },
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as RootState;
      const { latitude, longitude, locationId } = params;

      // Get the preferred units
      const units = selectPreferredUnits(state);

      // Fetch data
      const data = await getCurrentWeather(latitude, longitude, units);

      return {
        locationId,
        data,
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(
          error.response.data.message || "Failed to fetch weather data",
        );
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Unknown error occurred");
    }
  },
);

export const weatherSlice = createSlice({
  name: "weather",
  initialState,
  reducers: {
    setAutoRefresh: (state, action) => {
      state.autoRefresh = action.payload;
    },
    setRefreshInterval: (state, action) => {
      state.refreshInterval = action.payload;
    },
    clearWeatherData: (state) => {
      state.data = null;
      state.error = null;
    },
    invalidateCache(state) {
      state.cachedLocations = {};
    },
    setCurrentWeatherFromCache: (state, action: PayloadAction<string>) => {
      const cacheKey = action.payload;
      const cachedData = state.cachedLocations[cacheKey];

      if (cachedData) {
        state.data = cachedData.data;
        state.lastUpdated = cachedData.timestamp;
      }
    },
    setLocations: (state, action: PayloadAction<Location[]>) => {
      state.locations = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Handle fetchWeather
    builder
      .addCase(fetchWeather.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWeather.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.lastUpdated = new Date().toISOString();

        // Cache the data for this location
        if (action.payload?.location?.latitude && action.payload?.location?.longitude) {
          const { latitude, longitude } = action.payload.location;
          const cacheKey = `${latitude.toFixed(4)},${longitude.toFixed(4)}`;

          state.cachedLocations[cacheKey] = {
            data: action.payload,
            timestamp: new Date().toISOString(),
          };
        }
      })
      .addCase(fetchWeather.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch weather data';
      });

    // Handle cacheLocationWeather
    builder.addCase(cacheLocationWeather.fulfilled, (state, action) => {
      const { locationId, data } = action.payload;
      
      if (data.location && typeof data.location.latitude === 'number' && typeof data.location.longitude === 'number') {
        const { latitude, longitude } = data.location;
        const cacheKey = `${latitude.toFixed(4)},${longitude.toFixed(4)}`;

        // Store in cache
        state.cachedLocations[cacheKey] = {
          data,
          timestamp: new Date().toISOString(),
        };
      }
    });
  },
});

export const {
  setAutoRefresh,
  setRefreshInterval,
  clearWeatherData,
  invalidateCache,
  setCurrentWeatherFromCache,
  setLocations,
} = weatherSlice.actions;

// Selectors
export const selectWeatherData = (state: RootState) => state.weather.data;
export const selectWeatherLoading = (state: RootState) => state.weather.loading;
export const selectWeatherError = (state: RootState) => state.weather.error;
export const selectWeatherLastUpdated = (state: RootState) =>
  state.weather.lastUpdated;
export const selectWeatherAutoRefresh = (state: RootState) =>
  state.weather.autoRefresh;
export const selectWeatherRefreshInterval = (state: RootState) =>
  state.weather.refreshInterval;
export const selectCachedLocations = (state: RootState) =>
  state.weather.cachedLocations;
export const selectLocations = (state: { weather: WeatherState }): Location[] =>
  state.weather.locations;

// Derived selectors with safe property access
export const selectCurrentTemperature = (state: { weather: WeatherState }) =>
  state.weather.data?.temperature.actual || 0;
export const selectCurrentConditions = (state: { weather: WeatherState }) =>
  state.weather.data?.conditions[0] || null;
export const selectCloudCover = (state: { weather: WeatherState }) =>
  state.weather.data?.clouds || 0;
export const selectPrecipitationProbability = (state: RootState) =>
  state.weather.data?.precipitation?.probability;
export const selectWindSpeed = (state: RootState) =>
  state.weather.data?.wind?.speed;
export const selectMoonPhase = (state: RootState) =>
  state.weather.data?.astronomy?.moonPhase;
export const selectSunriseTime = (state: RootState) =>
  state.weather.data?.astronomy?.sunrise;
export const selectSunsetTime = (state: RootState) =>
  state.weather.data?.astronomy?.sunset;
export const selectWeatherAlerts = (state: RootState) =>
  state.weather.data?.alerts || [];
export const selectHasActiveWeatherAlerts = (state: RootState) =>
  (state.weather.data?.alerts?.length || 0) > 0;

// Helper utility to get a human-readable moon phase name from the numeric value
export const getMoonPhaseName = (phaseValue: number): string => {
  if (phaseValue >= 0 && phaseValue < 0.03) return "New Moon";
  if (phaseValue < 0.25) return "Waxing Crescent";
  if (phaseValue < 0.28) return "First Quarter";
  if (phaseValue < 0.47) return "Waxing Gibbous";
  if (phaseValue < 0.53) return "Full Moon";
  if (phaseValue < 0.72) return "Waning Gibbous";
  if (phaseValue < 0.78) return "Last Quarter";
  if (phaseValue < 0.97) return "Waning Crescent";
  return "New Moon";
};

// Helper to determine if it's currently daytime at the location
export const selectIsDaytime = (state: RootState) => {
  const data = state.weather.data;
  if (!data || !data.astronomy) return false;

  const now = new Date();
  const sunrise = data.astronomy.sunrise
    ? new Date(data.astronomy.sunrise)
    : null;
  const sunset = data.astronomy.sunset ? new Date(data.astronomy.sunset) : null;

  if (!sunrise || !sunset) return true; // Default to true if we don't have data

  return now >= sunrise && now <= sunset;
};

// Helper to get the best viewing score based on current conditions
export const selectViewingScore = (state: { weather: WeatherState }) => {
  const data = state.weather.data;
  if (!data) return 0;

  let score = 10;
  // Deduct points for poor conditions
  if (data.clouds.all > 50) score -= 3;
  if (data.precipitation.probability > 30) score -= 2;
  if (data.visibility < 5000) score -= 2;
  if (data.wind.speed > 20) score -= 1;
  return Math.max(0, score);
};

export default weatherSlice.reducer;
